import {useCallback} from 'react';
import {ResizeCallbackData} from 'react-resizable';

import {getRowsPerPageWithHeight} from './resizeUtils';
import {useWorkspaceSectionLayoutConfigs} from './WorkspaceSectionLayoutConfigs';

export const useResizableWorkspaceSection = ({
  panelsPerPage,
  setResizingSectionHeight,
  setRowsPerPage,
}: {
  panelsPerPage: number;
  setResizingSectionHeight: (height: number | null) => void;
  setRowsPerPage: (rowsPerPage: number) => void;
}) => {
  const {
    updateFlowConfig,
    maxRows,
    panelHeight,
    panelsPerRow,
    currentPage,
    setCurrentPage,
    initialRowsPerPage,
  } = useWorkspaceSectionLayoutConfigs();

  const handleOnResize = useCallback(
    (_, data: ResizeCallbackData) => {
      const sectionHeight = data.size.height;
      const newRowsPerPage = getRowsPerPageWithHeight({
        maxRows,
        panelHeight,
        sectionHeight,
      });

      setResizingSectionHeight(sectionHeight);
      setRowsPerPage(newRowsPerPage);
    },
    [maxRows, panelHeight, setRowsPerPage, setResizingSectionHeight]
  );

  const handleResizeStop = useCallback(
    (_, data: ResizeCallbackData) => {
      const sectionHeight = data.size.height;

      setResizingSectionHeight(null);

      const newRowsPerPage = getRowsPerPageWithHeight({
        maxRows,
        panelHeight,
        sectionHeight,
      });

      // Don't update configs if the number of rows hasn't changed.
      if (newRowsPerPage === initialRowsPerPage) {
        return;
      }

      const firstVisiblePanelIndex = panelsPerPage * currentPage;
      const newCurrentPage = Math.floor(
        firstVisiblePanelIndex / (newRowsPerPage * panelsPerRow)
      );

      setCurrentPage(newCurrentPage);
      setRowsPerPage(newRowsPerPage);
      updateFlowConfig({
        rowsPerPage: newRowsPerPage,
      });
    },
    [
      maxRows,
      panelHeight,
      panelsPerRow,
      initialRowsPerPage,
      panelsPerPage,
      currentPage,
      updateFlowConfig,
      setResizingSectionHeight,
      setCurrentPage,
      setRowsPerPage,
    ]
  );

  return {
    handleOnResize,
    handleResizeStop,
  };
};
